import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MapPin, Calendar, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GalleryPage() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        setGalleryItems(response.data);
      } catch (error) {
        console.error("Error fetching gallery:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const formatServiceType = (type) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const filteredItems = galleryItems.filter(item => {
    if (filter === "all") return true;
    return item.project_type === filter;
  });

  if (loading) {
    return (
      <div data-testid="gallery-page">
        <section className="bg-[#0F172A] py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase mb-6">
              Project Gallery
            </h1>
          </div>
        </section>
        <section className="py-16 lg:py-24 bg-[#F1F5F9]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="skeleton h-96 rounded-lg"></div>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="gallery-page">
      {/* Hero Section */}
      <section className="bg-[#0F172A] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block text-[#F59E0B] font-medium text-sm uppercase tracking-wider mb-4">
            Our Work
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase mb-6">
            Project Gallery
          </h1>
          <p className="text-slate-300 max-w-2xl mx-auto text-lg">
            Browse our portfolio of completed commercial and residential caulking projects
            across the Gold Coast and surrounding areas.
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 lg:py-24 bg-[#F1F5F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Tabs */}
          <Tabs defaultValue="all" className="mb-12" onValueChange={setFilter}>
            <TabsList className="bg-white shadow-sm">
              <TabsTrigger 
                value="all" 
                className="font-heading uppercase data-[state=active]:bg-[#0F172A] data-[state=active]:text-white"
                data-testid="gallery-filter-all"
              >
                All Projects
              </TabsTrigger>
              <TabsTrigger 
                value="commercial" 
                className="font-heading uppercase data-[state=active]:bg-[#0F172A] data-[state=active]:text-white"
                data-testid="gallery-filter-commercial"
              >
                Commercial
              </TabsTrigger>
              <TabsTrigger 
                value="residential" 
                className="font-heading uppercase data-[state=active]:bg-[#0F172A] data-[state=active]:text-white"
                data-testid="gallery-filter-residential"
              >
                Residential
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item) => (
              <Card 
                key={item.id} 
                className="gallery-item group border-0 shadow-lg hover:shadow-xl overflow-hidden"
                data-testid={`gallery-detail-${item.id}`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute top-4 left-4 flex gap-2">
                    <Badge className="bg-[#F59E0B] text-[#0F172A] font-medium">
                      {formatServiceType(item.service_type)}
                    </Badge>
                    {item.featured && (
                      <Badge className="bg-[#0F172A] text-white font-medium">
                        Featured
                      </Badge>
                    )}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-heading text-xl font-bold text-[#0F172A] uppercase mb-3">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                    {item.description}
                  </p>
                  <div className="flex items-center justify-between text-sm text-slate-500">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{item.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{item.year}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-500">No projects found for this category.</p>
            </div>
          )}

          {/* More Projects Indicator */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 bg-[#0F172A] text-white px-6 py-4 rounded-xl">
              <span className="font-heading text-3xl font-bold text-[#F59E0B]">500+</span>
              <div className="text-left">
                <p className="font-medium">Projects Completed</p>
                <p className="text-sm text-slate-400">Across Queensland</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white uppercase mb-6">
            Like What You See?
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto text-lg mb-8">
            Let us bring the same quality and professionalism to your project.
            Contact us today for a free quote.
          </p>
          <Link to="/quote">
            <Button
              size="lg"
              className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0F172A] font-heading font-bold uppercase tracking-wide px-8"
              data-testid="gallery-cta-btn"
            >
              Request a Free Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
